import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ImageValidationOptions {
  allowedTypes?: string[];
  maxFileSizeMB?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  requireSquare?: boolean;
  squareTolerance?: number; // 0.05 = 5%
}

const defaultOptions: Required<ImageValidationOptions> = {
  allowedTypes: ['image/png', 'image/jpeg'],
  maxFileSizeMB: 1,
  minWidth: 1,
  minHeight: 1,
  maxWidth: Infinity,
  maxHeight: Infinity,
  requireSquare: false,
  squareTolerance: 0.05,
};

export const useImageValidator = (options?: ImageValidationOptions) => {
  const { t } = useTranslation();

  const {
    allowedTypes,
    maxFileSizeMB,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    requireSquare,
    squareTolerance,
  } = { ...defaultOptions, ...options };

  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const currentImageUrl = useRef<string | null>(null);

  const clearPreview = () => {
    setPreviewUrl(null);
    if (currentImageUrl.current) {
      URL.revokeObjectURL(currentImageUrl.current);
      currentImageUrl.current = null;
    }
  };

  const validate = useCallback(
    async (file: File): Promise<boolean> => {
      clearPreview();

      if (!allowedTypes.includes(file.type)) {
        setError(
          t('Only the following formats are allowed: {{allowedTypes}}', {
            allowedTypes: allowedTypes.join(', '),
          }),
        );
        return false;
      }

      const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setError(
          t('Image must be smaller than {{maxFileSizeMB}}MB.', {
            maxFileSizeMB,
          }),
        );
        return false;
      }

      const image = new Image();
      const objectUrl = URL.createObjectURL(file);

      const result = await new Promise<boolean>((resolve) => {
        image.onload = () => {
          const { width, height } = image;

          if (width < minWidth || height < minHeight) {
            setError(
              t('Image must be at least {{minWidth}}x{{minHeight}}px.', {
                minWidth,
                minHeight,
              }),
            );
            URL.revokeObjectURL(objectUrl);
            return resolve(false);
          }

          if (width > maxWidth || height > maxHeight) {
            setError(
              t('Image must not exceed {{maxWidth}}x{{maxHeight}}px.', {
                maxWidth,
                maxHeight,
              }),
            );
            URL.revokeObjectURL(objectUrl);
            return resolve(false);
          }

          if (requireSquare) {
            const ratio = width / height;
            if (Math.abs(1 - ratio) > squareTolerance) {
              setError(t('Image must be approximately square.'));
              URL.revokeObjectURL(objectUrl);
              return resolve(false);
            }
          }

          setError(null);
          setPreviewUrl(objectUrl);
          currentImageUrl.current = objectUrl;
          resolve(true);
        };

        image.onerror = () => {
          setError(t('Invalid image file.'));
          URL.revokeObjectURL(objectUrl);
          resolve(false);
        };

        image.src = objectUrl;
      });

      return result;
    },
    [
      allowedTypes,
      maxFileSizeMB,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      requireSquare,
      squareTolerance,
      t,
    ],
  );

  useEffect(() => {
    return () => {
      clearPreview();
    };
  }, []);

  return { validate, error, previewUrl, clear: clearPreview };
};
